import {
  GridApiRef,
  GridCallbackDetails,
  GridRowId,
  GridRowParams,
  GridSelectionModel,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ISelectedEntity } from '../../../components/unitoolbar/actions/interfaces';

/**
 * Default cap on simultaneously selected rows across all worklist-style grids.
 * Single source of truth — consumers should import this rather than redeclare.
 *
 * ⚠️ CHANGING THIS VALUE HAS WIDE EFFECTS — REVIEW BEFORE TOUCHING:
 *
 *   1. Backend bulk-action endpoints (skift sagsbehandler, send brev, hent
 *      som regneark, etc.) are sized to accept this many entity ids per call.
 *      Raising the cap without raising the server-side limit will surface as
 *      400/413 errors on bulk operations.
 *   2. The UniToolbar action files (src/components/unitoolbar/actions/*.tsx)
 *      hardcode the same number in their `isDisabled` predicates and the
 *      Danish error string "Antallet af valgte elementer må ikke overstige
 *      250". Those must be updated in lockstep (or — better — refactored to
 *      import SELECTION_CAP from here).
 *   3. The WorklistPagination chip ("{n} / 250 valgt") reads SELECTION_CAP
 *      via paginationSelectionInfo.selectionCap; that path picks up the new
 *      value automatically, but the visual budget assumes ≤ ~3 digits.
 *   4. The MUI DataGridPro `keepNonExistentRowsSelected` path scales O(n)
 *      with cap on every selection-model change, and the apiRef sync-back
 *      effect serialises the Map keys on every update. Anything above a few
 *      thousand will start to feel sluggish on slower client machines.
 */
export const SELECTION_CAP = 250;

/**
 * Cross-page row selection backed by a Map<entityId, ISelectedEntity> with a
 * cumulative cap. Encapsulates the subtle ordering rules and reference-stability
 * tricks that the MUI DataGridPro selection model demands once selections need
 * to persist across pagination:
 *
 *   - Selections from non-visible pages must survive page changes (we never
 *     mutate entries outside the current page's row ids).
 *   - When a "select all" lands on a new page, we must apply deletions before
 *     additions; otherwise the additions can exhaust the cap with rows that
 *     are about to be removed.
 *   - `isRowSelectable` has to be a stable reference, or MUI re-runs its
 *     selection-cleanup useEffect on every render and crashes mid-pagination
 *     with "No row with id #...".
 *   - MUI's own selection model may temporarily exceed our cap (it filters
 *     visible ids past `isRowSelectable` after they've all been added); we
 *     replay our capped Map back into MUI via `apiRef.setSelectionModel` so
 *     the visible checkboxes don't lie.
 */
export interface ICappedRowSelection {
  /** The capped selection state, keyed by entityId. */
  selectedRowsByEntityId: Map<string, ISelectedEntity>;
  /** Memoized array view of `selectedRowsByEntityId.values()`. */
  selectedEntities: ISelectedEntity[];
  /** Pass to <DataGrid onSelectedRow={...} />. */
  handleSelectedRowChange: (model: GridSelectionModel, details: GridCallbackDetails<any>) => void;
  /** Pass to <DataGrid isRowSelectable={...} />. */
  isRowSelectable: (params: GridRowParams) => boolean;
  /** Drop all selections — e.g. wired to a "Ryd markering" button. */
  clearSelection: () => void;
}

export interface IUseCappedRowSelectionParams {
  cap: number;
  apiRef: GridApiRef;
  /**
   * Fingerprint of the current search criteria — when this changes, the Map is
   * dropped. With `keepNonExistentRowsSelected` enabled, MUI no longer
   * auto-clears selection when the result set changes, so the consumer has to
   * tell us which prop changes mean "different criteria" (filter, column set,
   * result type) vs "same criteria, different page/sort". Pagination and sort
   * must NOT contribute to the key — they're the cases the Map exists to
   * survive.
   */
  resetKey?: string;
}

export function useCappedRowSelection({ cap, apiRef, resetKey }: IUseCappedRowSelectionParams): ICappedRowSelection {
  const [selectedRowsByEntityId, setSelectedRowsByEntityId] = useState<Map<string, ISelectedEntity>>(new Map());

  // Drop accumulated selection when the criteria fingerprint changes. The
  // mounted ref skips the initial render so we don't set an already-empty Map
  // to a fresh empty Map.
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    setSelectedRowsByEntityId(new Map());
  }, [resetKey]);

  const selectedEntities = useMemo(() => Array.from(selectedRowsByEntityId.values()), [selectedRowsByEntityId]);

  const handleSelectedRowChange = useCallback(
    (model: GridSelectionModel, details: GridCallbackDetails<any>) => {
      const entityIdsInModel = new Set(model.map((rowId: GridRowId) => rowId.toString()));
      // getRowModels() returns every row the grid currently holds in memory.
      // - mode="server": only the current page's rows.
      // - mode="client": every row across every page.
      // The two-phase loop below works correctly in both modes because `model`
      // from onSelectionModelChange covers the same set of ids that
      // getRowModels() does (when keepNonExistentRowsSelected is set), so we
      // only ever inspect ids we have authoritative state for.
      const rowIdsTrackedByGrid = Array.from(details.api.getRowModels().keys()).map(rowId => (rowId as GridRowId).toString());

      setSelectedRowsByEntityId(previousSelectedRowsByEntityId => {
        const updatedSelectedRowsByEntityId = new Map(previousSelectedRowsByEntityId);
        let hasChanged = false;

        // Phase 1 — deletions first so they free up cap slots for the additions
        // below. Only mutate entries for rows currently tracked by the grid;
        // selections from non-tracked pages (server mode) are intentionally
        // left untouched so they survive pagination.
        rowIdsTrackedByGrid.forEach(entityId => {
          if (!entityIdsInModel.has(entityId) && updatedSelectedRowsByEntityId.delete(entityId)) {
            hasChanged = true;
          }
        });

        // Phase 2 — additions, enforcing the cap cumulatively. The
        // isRowSelectable predicate caps individual clicks, but a "select all"
        // on a new page filters every tracked id past the predicate before any
        // of them lands in state, so the cumulative cap has to be enforced here.
        rowIdsTrackedByGrid.forEach(entityId => {
          if (
            entityIdsInModel.has(entityId) &&
            !updatedSelectedRowsByEntityId.has(entityId) &&
            updatedSelectedRowsByEntityId.size < cap
          ) {
            const row = details.api.getRow(entityId);
            if (row) {
              updatedSelectedRowsByEntityId.set(entityId, {
                entityId,
                rootEntityId: row.rootEntityId,
                row,
              });
              hasChanged = true;
            }
          }
        });

        return hasChanged ? updatedSelectedRowsByEntityId : previousSelectedRowsByEntityId;
      });
    },
    [cap]
  );

  // Mirror the Map into a ref so isRowSelectable stays a stable reference
  // (re-creating it on every selection change retriggers MUI's selection-
  // cleanup useEffect and surfaces as "No row with id #..." mid-pagination).
  const selectedRowsByEntityIdRef = useRef(selectedRowsByEntityId);
  useEffect(() => {
    selectedRowsByEntityIdRef.current = selectedRowsByEntityId;
  }, [selectedRowsByEntityId]);

  const isRowSelectable = useCallback(
    (params: GridRowParams) => {
      const currentSelection = selectedRowsByEntityIdRef.current;
      const entityId = params.id?.toString();
      // Always allow already-selected rows so the user can deselect them.
      if (entityId && currentSelection.has(entityId)) return true;
      return currentSelection.size < cap;
    },
    [cap]
  );

  // Replay our (capped) Map back into MUI's selection model whenever the Map
  // changes. apiRef.current may exist as a stub before the grid mounts and
  // registers its API, so guard with a typeof check.
  useEffect(() => {
    if (typeof apiRef.current?.setSelectionModel !== 'function') return;
    apiRef.current.setSelectionModel(Array.from(selectedRowsByEntityId.keys()));
  }, [selectedRowsByEntityId, apiRef]);

  const clearSelection = useCallback(() => {
    setSelectedRowsByEntityId(new Map());
  }, []);

  return {
    selectedRowsByEntityId,
    selectedEntities,
    handleSelectedRowChange,
    isRowSelectable,
    clearSelection,
  };
}
